/**
 * WelcomeKatrina — Prototype 1
 * Route: /prototypes/welcome-katrina
 *
 * Katrina is being welcomed *into a relationship*. She is not logging into
 * software. The screen must feel personal, quiet, and considered. Presence
 * is present in its resting form — not moving, not performing — carrying
 * the meaning of "I'm here with you" without asking for attention.
 */
import React from "react";
import PrototypeShell from "./PrototypeShell";
import PresenceForm from "./PresenceForm";

export default function WelcomeKatrina() {
  const notes = {
    communicates:
      "This is a welcome from a company and a companion, not a login handshake. Katrina reads her own name, sees she is entering something considered, and is invited — not funneled — to begin.",
    placeholder: [
      "The line 'A moment of your time to introduce ourselves' is placeholder; final invitation copy is Katrina's to approve.",
      "Toto is present as a name only. No avatar. No mascot rendering. Presence itself carries Toto's meaning here.",
    ],
    decisions: [
      "Is the primary action labelled 'Begin' or something more relational (e.g. 'I'm ready')?",
      "Does the screen sit inside Toto's shell (no) or open on a raw canvas (currently: raw canvas)?",
      "Should Katrina's role or company appear visibly (e.g. 'Chief Operating Officer, Rainbow Interests')? Currently: no — too corporate.",
    ],
    assumptions: [
      "Katrina's first name is enough. Last name feels formal, unearned.",
      "No progress dots, no 'step 1 of N' — this is not a wizard.",
      "Rainbow Interests identity uses the wordmark treatment already established in the sign-in surface.",
    ],
  };

  return (
    <PrototypeShell title="Prototype 01 · Katrina Welcome" notes={notes}>
      <section className="min-h-[calc(100vh-6rem)] px-8 lg:px-16 flex items-center" data-testid="welcome-katrina">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center w-full max-w-6xl mx-auto">
          {/* Left column — Presence at rest */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start">
            <PresenceForm size={360} tone="warm" testId="welcome-presence" />
          </div>

          {/* Right column — the welcome itself */}
          <div className="lg:col-span-7">
            <h1
              className="font-serif-display text-6xl lg:text-7xl leading-[0.98] text-stone-900"
              style={{ fontWeight: 400 }}
              data-testid="welcome-greeting"
            >
              Welcome,
              <br />Katrina.
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed mt-8 max-w-lg" data-testid="welcome-body">
              I'm Toto. Before you see anything the team has been building, I'd
              like a moment of your time to introduce ourselves properly.
            </p>
            <div className="mt-12">
              <button
                className="px-8 py-3 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 transition-colors"
                data-testid="welcome-begin"
                onClick={(e) => e.preventDefault()}
              >
                Begin
              </button>
              <div className="text-[11px] text-stone-400 italic mt-3">
                (prototype — action is inert; no navigation)
              </div>
            </div>
          </div>
        </div>
      </section>
    </PrototypeShell>
  );
}
